/**
 * Containers API — typed wrapper around /v3/containers (= course folders).
 *
 * In the Parse backend:
 *   Container = a folder that holds child containers and/or slides (modules).
 *   Slide = a module (the actual content leaf).
 *
 * Real endpoints from legacy ContainersAPI.js:
 *   GET  /v3/containers/all              → flat list of all user's containers
 *   GET  /v3/containers/:id              → single container
 *   GET  /v3/containers/:id/children     → children (sub-containers + slide refs)
 *   POST /v3/containers?parentId=xxx     → create new container in parent
 *   PUT  /v3/containers/:id              → rename / update container
 *   DELETE /v3/containers/:id            → delete container
 */
import apiClient from './client';

/* ── Types ─────────────────────────────────────────────────────────────────── */

export interface CourseContainer {
  id: string;
  name?: string;
  description?: string;
  /** IDs of child containers (API returns as 'childrenIds') */
  childrenIds?: string[];
  /** Legacy alias */
  childContainersIds?: string[];
  /** IDs of modules inside this container (API returns as 'modulesIds') */
  modulesIds?: string[];
  /** Legacy alias */
  slidesIds?: string[];
  /** Populated children returned by /children endpoint */
  children?: CourseContainer[];
  /** Populated slides (short) returned by /children endpoint */
  slides?: ContainerSlideShort[];
  parentId?: string;
  ownerId?: string;
  creatorId?: string;
  access?: string;
  timestamp?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

/** Short (list) representation of a slide/module */
export interface ContainerSlideShort {
  id: string;
  name?: string;
  description?: string;
  imageUrl?: string;
  avatar?: string;
  access?: string;
  timestamp?: number;
  isDeleted?: boolean;
  [key: string]: unknown;
}

/* ── Persistent cache for getAll (survives page reloads / HMR) ────────────── */

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY = 'ep_containers_cache';

// In-memory L1 cache (fastest)
let _allCache: { data: CourseContainer[]; ts: number } | null = null;

function readSessionCache(): { data: CourseContainer[]; ts: number } | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data: CourseContainer[]; ts: number };
    if (Date.now() - parsed.ts > CACHE_TTL) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
}

function writeSessionCache(data: CourseContainer[]) {
  try {
    const entry = { data, ts: Date.now() };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    _allCache = entry;
  } catch { /* sessionStorage full — skip */ }
}

/** Invalidate the cache (call after create/update/delete). */
export function invalidateContainersCache() {
  _allCache = null;
  try {
    sessionStorage.removeItem(CACHE_KEY);
    Object.keys(sessionStorage).forEach(k => {
      if (k.startsWith('ep_slides_') || k.startsWith('ep_courses_tree_')) {
        sessionStorage.removeItem(k);
      }
    });
  } catch { /* ignore */ }
}

/* ── API ─────────────────────────────────────────────────────────────────── */

const ContainersAPI = {
  /** Get flat list of all accessible containers (cached for 5 min in memory + sessionStorage). */
  async getAll(): Promise<CourseContainer[]> {
    // L1: in-memory (same JS session)
    if (_allCache && Date.now() - _allCache.ts < CACHE_TTL) {
      return _allCache.data;
    }
    // L2: sessionStorage (survives HMR / Vite hot reload)
    const session = readSessionCache();
    if (session) {
      _allCache = session;
      console.log(`[ContainersAPI] getAll from sessionStorage cache (${session.data.length} items)`);
      return session.data;
    }
    // L3: network
    const { data } = await apiClient.get<CourseContainer[]>('/v3/containers/all');
    const result = (data ?? []) as CourseContainer[];
    writeSessionCache(result);
    return result;
  },

  /** Get a single container by ID */
  async getById(id: string): Promise<CourseContainer> {
    const { data } = await apiClient.get<CourseContainer>(`/v3/containers/${id}`);
    return data;
  },

  /** Get immediate children (sub-containers + slides) of a container */
  async getChildren(id: string): Promise<CourseContainer> {
    const { data } = await apiClient.get<CourseContainer>(
      `/v3/containers/${id}/children`,
      { params: { extended: 1 } }
    );
    return data;
  },

  /** Create a new container inside a parent */
  async create(
    parentId: string | null,
    payload: Partial<CourseContainer>
  ): Promise<CourseContainer> {
    const { data } = await apiClient.post<CourseContainer>(
      '/v3/containers',
      payload,
      parentId ? { params: { parentId } } : undefined
    );
    invalidateContainersCache();
    return data;
  },

  /** Rename or update a container */
  async update(
    id: string,
    payload: Partial<CourseContainer>
  ): Promise<CourseContainer> {
    const { data } = await apiClient.put<CourseContainer>(
      `/v3/containers/${id}`,
      { id, ...payload }
    );
    invalidateContainersCache();
    return data;
  },

  /** Delete a container */
  async delete(id: string, parentId?: string): Promise<void> {
    await apiClient.delete(`/v3/containers/${id}`, {
      params: parentId ? { parentId } : undefined,
    });
    invalidateContainersCache();
  },
};

export default ContainersAPI;
